import { PagingResponse } from '@/@types/responses/common/PagingResponse'
import { AppConfig } from '@/app.config'
import authenticatedFetch from '@/authenticatedFetch'
import { PurchaseOrder } from '@/@types/responses/purchase_order/PurchaseOrder'
import { PurchaseOrderDetails } from '@/@types/responses/purchase_order/PurchaseOrderDetails'
import { CreatePurchaseOrderMessage } from '@/@types/messages/CreatePurchaseOrderMessage'
import { NoteMessage } from '@/@types/messages/NoteMessage'
import { UpdatePurchaseOrderMessage } from '@/@types/messages/UpdatePurchaseOrderMessage'
import { MiscellaneousService } from './miscellaneous.service'
import { CreatePurchaseOrderItemMessage } from '@/@types/messages/CreatePurchaseOrderItemMessage'
import { ObjectCreatedResponse } from '@/@types/responses/common/ObjectCreatedResponse'
import { ObjectsCreatedResponse } from '@/@types/responses/common/ObjectsCreatedResponse'

export class PurchaseOrderService {
  private appConfig: AppConfig
  private miscellaneousService: MiscellaneousService

  constructor(miscellaneousService: MiscellaneousService) {
    this.appConfig = new AppConfig()
    this.miscellaneousService = miscellaneousService
  }

  public createPurchaseOrder(createPurchaseOrderMessage: CreatePurchaseOrderMessage): Promise<ObjectCreatedResponse<number>> {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(createPurchaseOrderMessage)
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/purchase-orders`, this.miscellaneousService, requestOptions).then(async response => {
      const json = await response.json()
      return new ObjectCreatedResponse<number>(json.id)
    })
  }

  public createPurchaseOrderItem(purchaseOrderId: number, createPurchaseOrderItemMessage: CreatePurchaseOrderItemMessage): Promise<ObjectsCreatedResponse<number>> {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(createPurchaseOrderItemMessage)
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/purchase-orders/${purchaseOrderId}/purchase-order-items`, this.miscellaneousService, requestOptions).then(async response => {
      const json = await response.json()
      return new ObjectsCreatedResponse<number>(json.ids)
    })
  }

  public createPurchaseOrderNote(purchaseOrderId: number, noteMessage: NoteMessage): Promise<ObjectCreatedResponse<number>> {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(noteMessage)
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/purchase-orders/${purchaseOrderId}/notes`, this.miscellaneousService, requestOptions).then(async response => {
      const json = await response.json()
      return new ObjectCreatedResponse<number>(json.id)
    })
  }

  public deletePurchaseOrder(id: number): Promise<void> {
    const requestOptions = {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' }
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/purchase-orders/${id}`, this.miscellaneousService, requestOptions).then(() => {
      return Promise.resolve()
    })
  }

  public getPurchaseOrder(id: number): Promise<PurchaseOrderDetails> {
    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/purchase-orders/${id}`, this.miscellaneousService).then(async response => {
      const json = await response.json()
      return new PurchaseOrderDetails(json.id, json.canAddItems, json.canReceiveAll, json.description, json.setACalendarDate, json.poNumber, json.venderPoNumber, json.submitted, json.submittedDate, json.submittedByUser, json.verified, json.verifiedDate, json.verifiedByUser, json.supplierId, json.notes, json.purchaseOrderItems, json.createdByUser, json.createdDate, json.lastModifiedByUser, json.lastModifiedDate)
    })
  }

  public receiveAllPurchaseOrderItems(id: number): Promise<void> {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/purchase-orders/${id}/purchase-order-items/receive-all-purchase-order-items`, this.miscellaneousService, requestOptions).then(() => {
      return Promise.resolve()
    })
  }

  public searchPurchaseOrders(searchTerm: string, pageNumber: number, pageSize: number, sortField: string, ascending: boolean): Promise<PagingResponse<PurchaseOrder>> {
    const queryString = `?searchTerm=${searchTerm}&pageNumber=${pageNumber}&pageSize=${pageSize}&sortField=${sortField}&ascending=${ascending}`

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/purchase-orders${queryString}`, this.miscellaneousService).then(async response => {
      const json = await response.json()
      return json as PagingResponse<PurchaseOrder>
    })
  }

  public updatePurchaseOrder(purchaseOrderId: number, updatePurchaseOrderMessage: UpdatePurchaseOrderMessage): Promise<void> {
    const requestOptions = {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatePurchaseOrderMessage)
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/purchase-orders/${purchaseOrderId}`, this.miscellaneousService, requestOptions).then(() => {
      return Promise.resolve()
    })
  }
}
